import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../accounts/AccountPicker';
import { ContactPicker } from '../contacts/ContactPicker';
import { formatCurrency, todayIso } from '../../lib/formatters';

export interface BillLineDraft {
  accountId: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

export interface BillFormValues {
  vendorId: string;
  vendorReference: string;
  billDate: string;
  dueDate: string;
  terms: string;
  memo: string;
  lines: BillLineDraft[];
}

export interface BillFormSubmit {
  vendorId: string;
  vendorReference?: string;
  billDate: string;
  dueDate: string;
  terms?: string;
  memo?: string;
  lines: { accountId: string; description?: string; quantity: string; unitPrice: string }[];
}

interface Props {
  companyId: string;
  initial?: Partial<BillFormValues>;
  submitLabel: string;
  submitting: boolean;
  errorMessage?: string;
  cancelTo: string;
  onSubmit: (input: BillFormSubmit) => void;
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const emptyLine = (): BillLineDraft => ({ accountId: '', description: '', quantity: '1', unitPrice: '' });

export function BillForm({ companyId, initial, submitLabel, submitting, errorMessage, cancelTo, onSubmit }: Props) {
  const { data: accounts = [] } = useAccounts(companyId);

  const [vendorId, setVendorId] = useState(initial?.vendorId ?? '');
  const [vendorReference, setVendorReference] = useState(initial?.vendorReference ?? '');
  const [billDate, setBillDate] = useState(initial?.billDate ?? todayIso());
  const [dueDate, setDueDate] = useState(initial?.dueDate ?? addDays(initial?.billDate ?? todayIso(), 30));
  const [terms, setTerms] = useState(initial?.terms ?? 'Net 30');
  const [memo, setMemo] = useState(initial?.memo ?? '');
  const [lines, setLines] = useState<BillLineDraft[]>(
    initial?.lines && initial.lines.length > 0 ? initial.lines : [emptyLine()],
  );

  // Bills usually debit expense/COGS, but may also debit an asset (prepaid, inventory,
  // fixed asset), so fall back to any postable account when no expense accounts exist.
  const hasExpenseAccounts = accounts.some(
    (a) =>
      a.isActive &&
      a.allowDirectPosting &&
      (a.accountType === 'expense' || a.accountType === 'cogs' || a.accountType === 'other_expense'),
  );
  // Keep any account already on a line visible so existing selections render and can be changed.
  const usedIds = new Set(lines.map((l) => l.accountId).filter(Boolean));
  const isAllocSelectable = (a: typeof accounts[number]) => {
    if (usedIds.has(a.id)) return true;
    if (!a.isActive || !a.allowDirectPosting) return false;
    return hasExpenseAccounts
      ? a.accountType === 'expense' || a.accountType === 'cogs' || a.accountType === 'other_expense'
      : true;
  };

  const lineAmount = (l: BillLineDraft) => (parseFloat(l.quantity) || 0) * (parseFloat(l.unitPrice) || 0);
  const total = lines.reduce((s, l) => s + lineAmount(l), 0);

  const setLine = (i: number, patch: Partial<BillLineDraft>) =>
    setLines((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const valid = vendorId && total > 0 && lines.some((l) => l.accountId && lineAmount(l) > 0);

  const submit = () => {
    onSubmit({
      vendorId,
      vendorReference: vendorReference.trim() || undefined,
      billDate,
      dueDate,
      terms: terms.trim() || undefined,
      memo: memo.trim() || undefined,
      lines: lines
        .filter((l) => l.accountId && lineAmount(l) > 0)
        .map((l) => ({
          accountId: l.accountId,
          description: l.description.trim() || undefined,
          quantity: String(parseFloat(l.quantity) || 0),
          unitPrice: String(parseFloat(l.unitPrice) || 0),
        })),
    });
  };

  return (
    <>
      <div className="card" style={{ marginBottom: 16, padding: 16, maxWidth: 760 }}>
        <div className="form-grid">
          <label htmlFor="vend">Vendor</label>
          <ContactPicker
            companyId={companyId}
            value={vendorId}
            onChange={setVendorId}
            contactType="vendor"
            defaultNewType="vendor"
            placeholder="— select a vendor —"
            id="vend"
          />

          <label htmlFor="vref">Vendor ref #</label>
          <input
            id="vref"
            value={vendorReference}
            onChange={(e) => setVendorReference(e.target.value)}
            maxLength={100}
            placeholder="vendor's invoice number (optional)"
            style={{ maxWidth: 320 }}
          />

          <label htmlFor="bdate">Bill date</label>
          <input
            id="bdate"
            type="date"
            value={billDate}
            onChange={(e) => {
              setBillDate(e.target.value);
              setDueDate(addDays(e.target.value, 30));
            }}
            style={{ maxWidth: 200 }}
          />

          <label htmlFor="ddate">Due date</label>
          <input id="ddate" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={{ maxWidth: 200 }} />

          <label htmlFor="terms">Terms</label>
          <input id="terms" value={terms} onChange={(e) => setTerms(e.target.value)} maxLength={100} style={{ maxWidth: 280 }} />

          <label htmlFor="memo">Memo</label>
          <input id="memo" value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} />
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>Line items</h2>
        <table className="table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th>Expense account</th>
              <th>Description</th>
              <th style={{ textAlign: 'right', width: 80 }}>Qty</th>
              <th style={{ textAlign: 'right', width: 120 }}>Rate</th>
              <th style={{ textAlign: 'right', width: 130 }}>Amount</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <AccountPicker
                    accounts={accounts}
                    value={l.accountId}
                    onChange={(id) => setLine(i, { accountId: id })}
                    predicate={isAllocSelectable}
                  />
                </td>
                <td>
                  <input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} style={{ width: '100%' }} />
                </td>
                <td>
                  <input inputMode="decimal" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} style={{ width: '100%', textAlign: 'right' }} />
                </td>
                <td>
                  <input inputMode="decimal" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} style={{ width: '100%', textAlign: 'right' }} placeholder="0.00" />
                </td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(lineAmount(l).toFixed(2))}</td>
                <td>
                  <button className="btn-danger" onClick={() => removeLine(i)} disabled={lines.length <= 1}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={4}>Total</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(total.toFixed(2))}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
        <button className="btn-secondary" onClick={addLine}>+ Add line</button>

        {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn-primary" onClick={submit} disabled={!valid || submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </button>
          <Link to={cancelTo} className="btn-secondary" style={{ textDecoration: 'none' }}>Cancel</Link>
        </div>
      </div>
    </>
  );
}
